import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { CheckCheckIcon, BellIcon } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'

import { notificationsApi, type Notification } from '../services/notificationsApi'
import { useAuthStore } from '../store'
import { NotificationItem } from './NotificationItem'

type NotificationDropdownProps = {
  isOpen: boolean
  onClose: () => void
  portal?: string
}

export function NotificationDropdown({ isOpen, onClose, portal }: NotificationDropdownProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const activeOrgId = useAuthStore((s) => s.user?.organization_id)

  const { data, isLoading } = useQuery({
    queryKey: ['notifications-recent', activeOrgId, portal],
    queryFn: () => notificationsApi.list({ limit: 20, portal }),
    enabled: isOpen,
    staleTime: 10_000,
  })

  const markAllMutation = useMutation({
    mutationFn: () => notificationsApi.markAsRead(null),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications-recent'] })
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] })
    },
  })

  const dismissMutation = useMutation({
    mutationFn: (id: string) => notificationsApi.dismiss(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications-recent'] })
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] })
    },
  })

  const handleClick = (notification: Notification) => {
    if (!notification.is_read) {
      notificationsApi.markOneAsRead(notification.id).then(() => {
        queryClient.invalidateQueries({ queryKey: ['notifications-recent'] })
        queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] })
      })
    }
    if (notification.action_url) {
      navigate(notification.action_url)
    }
    onClose()
  }

  if (!isOpen) return null

  const notifications = data?.notifications ?? []
  const unreadCount = data?.unread_count ?? 0

  return (
    <div className="absolute top-full right-0 z-50 mt-2 w-96 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
        <h3 className="text-sm font-semibold text-gray-900">
          Notificacoes
          {unreadCount > 0 && (
            <span className="ml-2 inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
              {unreadCount}
            </span>
          )}
        </h3>
        {unreadCount > 0 && (
          <button
            type="button"
            onClick={() => markAllMutation.mutate()}
            disabled={markAllMutation.isPending}
            className="flex items-center gap-1 rounded px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 disabled:opacity-50"
          >
            <CheckCheckIcon className="h-3.5 w-3.5" />
            Marcar todas como lidas
          </button>
        )}
      </div>

      {/* List */}
      <div className="max-h-[400px] overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-gray-400">
            <BellIcon className="mb-2 h-8 w-8" />
            <p className="text-sm">Nenhuma notificação</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {notifications.map((n) => (
              <NotificationItem
                key={n.id}
                notification={n}
                onClick={handleClick}
                onDismiss={(id) => dismissMutation.mutate(id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div className="border-t border-gray-100 px-4 py-2">
          <Link
            to="/notifications"
            onClick={onClose}
            className="block text-center text-xs font-medium text-blue-600 hover:text-blue-700"
          >
            Ver todas as notificações
          </Link>
        </div>
      )}
    </div>
  )
}
